import React, { useMemo } from 'react';
import { Button, Dropdown } from 'antd';
import { Link } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import SortableList, { moveId } from '../common/SortableList';
import { buildActionMenu } from '../common/actionMenu';
import TagBadge from '../tags/TagBadge';
import { ARROW_DOWN, ARROW_UP, KEBAB } from '../icons';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { tagPagePath, tagPathLabel } from '../../utils/tags';

const pinId = pin => normalizeId(pin?._id);

/**
 * The ⋯ of one pin on the strip (PinnedStrip), in the taxonomy: Go to its
 * collection; Move up / Move down (the drag's keyboard- and tap-friendly
 * twin — offered only in reorder mode, like the grip); Edit tile… (a record
 * tile's values, KPR-1127); Change how many… (a list of records' count,
 * KPR-1128); Unpin from Home.
 */
export function pinMenu({ pin, ids, reordering, onReorder, onUnpin, onEditTile = null, onEditList = null, navigate }) {
	const id = pinId(pin);
	const index = ids.indexOf(id);
	const collectionId = normalizeId(pin.collection_id);
	return buildActionMenu([
		{
			key: 'collection',
			group: 'navigate',
			glyph: 'collection',
			label: `Go to ${pin.collection?.name || 'collection'}`,
			// A tag pin names its collection only where the person can see it
			// (a sub-collection reader's pin of a parent's tag names none).
			hidden: !collectionId || pin.targetType === 'collectionLink' || (pin.targetType === 'tag' && !pin.collection),
			onClick: () => navigate(`/collections/${collectionId}`),
		},
		{
			key: 'move-up',
			group: 'record',
			icon: <ARROW_UP />,
			label: 'Move up',
			disabled: index <= 0,
			hidden: !onReorder || !reordering,
			onClick: () => onReorder(moveId(ids, id, -1)),
		},
		{
			key: 'move-down',
			group: 'record',
			icon: <ARROW_DOWN />,
			label: 'Move down',
			disabled: index < 0 || index >= ids.length - 1,
			hidden: !onReorder || !reordering,
			onClick: () => onReorder(moveId(ids, id, 1)),
		},
		{
			// A record tile (KPR-1127): which values it shows.
			key: 'edit-tile',
			group: 'record',
			glyph: 'edit',
			label: 'Edit tile…',
			hidden: pin.targetType !== 'recordTile' || !onEditTile,
			onClick: () => onEditTile(pin),
		},
		{
			// A list of records (KPR-1128): how many it shows.
			key: 'edit-list',
			group: 'record',
			glyph: 'edit',
			label: 'Change how many…',
			hidden: pin.targetType !== 'recordList' || !onEditList,
			onClick: () => onEditList(pin),
		},
		{
			key: 'unpin',
			group: 'record',
			glyph: 'pin',
			label: 'Unpin from Home',
			hidden: !onUnpin,
			onClick: () => onUnpin(pin),
		},
	]);
}

export function RowMenu({ menu, name, className = '' }) {
	if (!menu) return null;
	return (
		<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
			<Button className={['home-pin__menu', className].filter(Boolean).join(' ')} icon={<KEBAB />} aria-label={`Actions for ${name}`} />
		</Dropdown>
	);
}

/**
 * The collections strip: the personal collection first and fixed, then the
 * favorite collections in the person's ONE order — `collections` arrives
 * already in it (useHome), the order the rail's Pinned and the collections
 * page's Favorites draw too, so a reorder here moves all three. Rows are
 * `{ id, collection }` with the COLLECTION's id; `onReorder(collectionIds)`
 * gets the favorites' new order (the personal collection never among them).
 */
export function HomeCollections({ personal, collections, reordering = false, onReorder }) {
	const items = useMemo(() => {
		const list = (collections || []).map(entry => ({ ...entry, id: normalizeId(entry.id) }));
		return personal
			? [{ id: `personal-${normalizeId(personal._id)}`, collection: personal, collectionId: normalizeId(personal._id), fixed: true }, ...list]
			: list;
	}, [personal, collections]);
	const fixed = useMemo(() => items.filter(item => item.fixed).map(item => item.id), [items]);
	return (
		<SortableList
			items={items}
			layout="grid"
			fixed={fixed}
			ariaLabel="Pinned collections"
			className="home-collections"
			getLabel={item => item.collection?.name || 'collection'}
			onReorder={(ids) => onReorder(ids.filter(id => !fixed.includes(id)))}
			reordering={reordering}
			renderItem={(item, { handle }) => (
				<div className="home-pin home-pin--collection">
					{handle}
					<RecordBadge
						kind="collection"
						record={item.collection}
						title={item.collection?.name || 'Untitled collection'}
						size="sm"
						to={`/collections/${item.collectionId || item.id}`}
					/>
				</div>
			)}
		/>
	);
}

/**
 * Collections grouped by private tag (tags T5, docs/plans/tags.md §3.6,
 * ruled d12): under the collections strip, one group per private tag that sits
 * on a collection — the tag's chip (its tag page) and how many, then its
 * collections, each linking to it. A collection shows in every group it
 * belongs to. The person makes the groups by putting their private tags on
 * collections; a collection with none stays as it is, in the strip or not.
 */
export function HomeCollectionGroups({ groups }) {
	const list = Array.isArray(groups) ? groups.filter(group => group?.tag && Array.isArray(group.collections) && group.collections.length) : [];
	if (!list.length) return null;
	return (
		<div className="home-tag-groups">
			{list.map(group => (
				<section key={normalizeId(group.tag._id)} className="home-tag-group" aria-label={`Collections tagged ${group.tag.name || 'with a private tag'}`}>
					<div className="home-tag-group__head">
						<TagBadge tag={group.tag} size="sm" to={tagPagePath(group.tag) || undefined} tooltip={tagPathLabel(group.tag)} />
						<span className="home-tag-group__count">{formatCount(group.collections.length, 'collection')}</span>
					</div>
					<ul className="home-tag-group__list">
						{group.collections.map(collection => (
							<li key={normalizeId(collection._id)} className="home-pin home-pin--collection">
								<RecordBadge
									kind="collection"
									record={collection}
									title={collection.name || 'Untitled collection'}
									size="sm"
									to={`/collections/${normalizeId(collection._id)}`}
								/>
							</li>
						))}
					</ul>
				</section>
			))}
		</div>
	);
}

/** Recently viewed: resolved rows (useResolvedViewHistory), newest first. */
export function HomeRecent({ rows }) {
	return (
		<ul className="home-recent">
			{rows.map(row => (
				<li key={row.key} className="home-recent__row">
					<RecordBadge
						kind={row.targetType}
						record={row.card}
						kicker={row.targetType === 'item' ? (row.card?.label?.singular || row.card?.name || undefined) : undefined}
						title={row.title}
						size="sm"
						to={row.path}
						collection={row.owner}
					/>
					{row.targetType !== 'item' ? <span className="home-recent__kind">{row.kind}</span> : null}
				</li>
			))}
		</ul>
	);
}

export function HomeMore({ to, children }) {
	return <Link className="collection-home__more" to={to}>{children}</Link>;
}
